import { Injectable, signal, inject } from "@angular/core";
import { SemesterService, type Semester } from "./semester.service";
import { AuthService } from "../../core/auth.service";

@Injectable({ providedIn: "root" })
export class SemesterStoreService {
  private readonly api = inject(SemesterService);
  private readonly authService = inject(AuthService);

  readonly semesters = signal<Semester[]>([]);
  readonly currentSemester = signal<Semester | null>(null);
  readonly loading = signal(false);

  constructor() {
    this.load();
  }

  private getUserId(): number {
    const info = this.authService.getUserInfo();
    return (info?.["userId"] as number) || 0;
  }

  load(): void {
    const uid = this.getUserId();
    this.loading.set(true);
    this.api.getEntityList({ userId: uid }).subscribe({
      next: (data) => {
        this.semesters.set(data);
        const current = data.find((s) => s.status === 1) || null;
        this.currentSemester.set(current);
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }

  setCurrent(id: number): void {
    const current = this.currentSemester();
    if (current && current.id !== id) {
      this.api
        .commonUpdate(current.id, [
          { id: current.id, op: "replace", path: "/Status", value: 0 },
        ])
        .subscribe();
    }
    this.api
      .commonUpdate(id, [{ id: id, op: "replace", path: "/Status", value: 1 }])
      .subscribe({
        next: () => this.load(),
      });
  }

  delete(id: number): void {
    this.api.delEntity(id).subscribe({
      next: () => this.load(),
    });
  }

  add(data: { name: string; startDate: string; endDate: string }): void {
    const isFirst = this.semesters().length === 0;
    const uid = this.getUserId();
    this.api
      .addEntity({
        name: data.name,
        startDate: data.startDate,
        endDate: data.endDate,
        status: isFirst ? 1 : 0,
        sortOrder: isFirst ? 1 : 0,
        userId: uid,
      })
      .subscribe({
        next: () => this.load(),
      });
  }

  update(
    id: number,
    data: { name: string; startDate: string; endDate: string; status: number },
  ): void {
    const uid = this.getUserId();
    this.api
      .editEntity({
        id,
        name: data.name,
        startDate: data.startDate,
        endDate: data.endDate,
        status: data.status,
        userId: uid,
      })
      .subscribe({
        next: () => this.load(),
      });
  }
}
